import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import { SEG_MAP } from '@/(routers)/boards/editor/block/definitions/fields/fieldSevenSegStr';
import {
    registerHelperFunction,
    registerSetupCode,
    resolveBoard,
    stmt,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    sevenSegGlobalCodes,
    sevenSegMarkUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/display/seven_seg_numer';

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const CHAR_TO_SEG_HELPER =
    `uint8_t charToSeg(char c) {\n` +
    `  switch(c) {\n` +
    Object.entries(SEG_MAP)
        .map(
            ([char, byte]) =>
                `    case '${char === "'" ? "\\'" : char === '\\' ? '\\\\' : char}': return 0x${byte
                    .toString(16)
                    .toUpperCase()
                    .padStart(2, '0')};\n`,
        )
        .join('') +
    `    default: return 0x00;\n` +
    `  }\n` +
    `}\n`;

export function register(gen: any): void {
    gen.forBlock['seven_seg_string'] = function (block: Blockly.Block): string {
        const position = block.getFieldValue('POSITION') as Position;
        const board = resolveBoard(block);
        const pins = BOARD_CONFIG[board].pins[position];
        const clk = pins.first;
        const dio = pins.second;
        const key = `${clk}_${dio}`;
        const instanceName = `num_display_${clk}_${dio}`;

        if (!sevenSegGlobalCodes.has(key)) {
            sevenSegGlobalCodes.set(key, `TM1637Display ${instanceName}(${clk}, ${dio});\n`);
            registerSetupCode(key, `  ${instanceName}.setBrightness(0x0f);\n`);
        }

        const byteValues = ['DIGIT_0', 'DIGIT_1', 'DIGIT_2', 'DIGIT_3'].map((name) => {
            const targetBlock = block.getInputTargetBlock(name);
            if (targetBlock && targetBlock.type !== 'string_seven_seg') {
                registerHelperFunction('charToSeg', CHAR_TO_SEG_HELPER);
                const strCode = gen.valueToCode(block, name, 0) || '""';
                return `charToSeg(${strCode}[0])`;
            }
            return gen.valueToCode(block, name, 0) || '0x00';
        });

        const blockId = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
        const varName = `seg_str_${clk}_${dio}_${blockId}`;
        const segArray = `uint8_t ${varName}[] = {${byteValues.join(', ')}};`;
        const setSegments = `${instanceName}.setSegments(${varName})`;

        sevenSegMarkUsed();
        return `${segArray}\n${stmt(setSegments)}`;
    };
}
